import { Trial, FVProfile, TrainingZones, QualityCheck } from '../types';

export function calculateFVProfile(trials: Trial[], massKg: number, vFreeMs: number, angleCorrection = 1): FVProfile {
  // Convert kgf to N with angle correction
  const forces = trials.map(t => t.load_kgf * 9.80665 * angleCorrection);
  const velocities = trials.map(t => t.v_max_ms);
  
  // Add free sprint point
  forces.push(0);
  velocities.push(vFreeMs);
  
  // Linear regression F = a + b*v
  const n = forces.length;
  const sumV = velocities.reduce((a, b) => a + b, 0);
  const sumF = forces.reduce((a, b) => a + b, 0);
  const sumVF = velocities.reduce((sum, v, i) => sum + v * forces[i], 0);
  const sumV2 = velocities.reduce((sum, v) => sum + v * v, 0);
  
  const slope = (n * sumVF - sumV * sumF) / (n * sumV2 - sumV * sumV);
  const intercept = (sumF - slope * sumV) / n;
  
  // Calculate R²
  const meanF = sumF / n;
  const ssRes = forces.reduce((sum, f, i) => {
    const predicted = intercept + slope * velocities[i];
    return sum + Math.pow(f - predicted, 2);
  }, 0);
  const ssTot = forces.reduce((sum, f) => sum + Math.pow(f - meanF, 2), 0);
  const R2 = 1 - (ssRes / ssTot);
  
  // Calculate F-V parameters
  const F0_N = intercept;
  const V0 = -intercept / slope;
  const Pmax = 0.25 * F0_N * V0;
  const F0_rel = F0_N / massKg;
  const Pmax_rel = Pmax / massKg;
  
  return {
    F0_N,
    F0_rel,
    V0,
    Pmax,
    Pmax_rel,
    R2,
    slope,
    intercept
  };
}

export function calculateTrainingZones(fvProfile: FVProfile, vFreeMs: number): TrainingZones {
  const { V0 } = fvProfile;
  
  // Calculate velocity at each zone boundary
  const vTech = vFreeMs * 0.9; // 10% v-loss
  const vSpeedStr = vFreeMs * 0.75; // 25% v-loss
  const vPower = vFreeMs * 0.6; // 40% v-loss
  const vStrSpeed = vFreeMs * 0.45; // 55% v-loss
  
  // Convert to loads using F = F0 + slope * v
  const getLoad = (v: number) => Math.max(0, (fvProfile.intercept + fvProfile.slope * v) / 9.80665);
  
  return {
    Technique: [0, getLoad(vTech)],
    SpeedStrength: [getLoad(vTech), getLoad(vSpeedStr)],
    Power: [getLoad(vSpeedStr), getLoad(vPower)],
    StrengthSpeed: [getLoad(vPower), getLoad(vStrSpeed)]
  };
}

export function checkDataQuality(trials: Trial[], fvProfile: FVProfile, vFreeMs: number): QualityCheck {
  const velocities = trials.map(t => t.v_max_ms);
  const minV = Math.min(...velocities);
  const maxV = Math.max(...velocities);
  const vRange = maxV - minV;
  const freeVRange = vFreeMs - minV;
  
  // Check uniform spacing (approximate)
  const sortedV = [...velocities].sort((a, b) => b - a);
  const spacings = sortedV.slice(1).map((v, i) => sortedV[i] - v);
  const avgSpacing = spacings.reduce((a, b) => a + b, 0) / spacings.length;
  const uniformSpacing = spacings.every(s => Math.abs(s - avgSpacing) / avgSpacing < 0.3);
  
  return {
    validR2: fvProfile.R2 >= 0.95,
    uniformSpacing,
    freeSprintPresent: vFreeMs > 0,
    sufficientDataPoints: trials.length >= 4,
    adequateVelocityRange: freeVRange / vFreeMs >= 0.5
  };
}